import React, { useRef } from 'react';

import { QuestionViewer } from 'pages/reports/components/QuestionViewer/QuestionViewer';
import { SanitizedQuestionStem } from 'pages/reports/components/SanitizedQuestionStem';
import { ChartInfoBox } from 'pages/reports/components/ChartInfoBox/ChartInfoBox';
import { TableGeneric } from 'pages/reports/components/TableGeneric/TableGeneric';
import { Report, ReportComponent, ReportSection, TestType } from 'apis/ReportApi';
import { SkillTree } from 'pages/reports/components/SkillTree/SkillTree';
import { GeneralGauge } from 'components/GeneralGaugeChart/GeneralGauge';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixTabs, GMetrixTabsTab } from 'components/GMetrixTabs';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { getReportIdFromPath } from 'utils/other';
import { useSuperState } from 'hooks';

import 'pages/reports/components/ChartInfoBox/styles/chart-info-box.scss';
import 'pages/reports/components/Report60/Report60.scss';

interface Report60Props {
    reportSections: ReportSection[];
    studentProfileId?: any;
}

const getAllQuestions = (data: any): any[] => {

    const result: any[] = [];

    function recurse(node: any) {

        if (Array.isArray(node)) {

            node.forEach(recurse);

        } else if (node && typeof node === 'object') {

            if (node.Title) {

                result.push(node);

            }

            Object.values(node).forEach(recurse);

        }

    }

    recurse(data);
    return result;

};

export const Report60 = ({
    reportSections,
    studentProfileId
}: Report60Props) => {

    const { formatMessage } = useIntl();
    const intl = useIntl();

    const [state, setState] = useSuperState({
        currentItem: undefined as any,
        currentSubReport: undefined as Report | undefined,
        selected: 0,
        showDetailsModal: false
    });

    const { currentItem, currentSubReport, selected, showDetailsModal } = state;

    let currentTabGroup: ReportSection[] = [];
    let tempGroup: ReportSection[] = [];
    const questionViewerRef = useRef<any>(null);

    const handleSelect = (index: number) => {

        setState({ selected: index });

    };

    const convertToFlatList = (component: ReportComponent, resultId: string, isInAppTest: boolean) => {

        // Flatten hierarchical data structure to a flat list
        const flattenData = (data: any[]): any[] => {

            const flattened: any[] = [];

            if (!data || !Array.isArray(data)) return flattened;

            data.forEach((theme: any) => {

                const themeName = theme.ThemeName || theme.text || 'Uncategorized';
                const themeItems = theme.items || theme.Items || [];

                themeItems.forEach((objective: any) => {

                    const objectiveItems = objective.items || objective.Items || [objective];

                    objectiveItems.forEach((item: any) => {

                        flattened.push({
                            ...item,
                            ObjectiveId: item.ObjectiveId || objective.ObjectiveId,
                            ObjectiveName: item.ObjectiveName || objective.ObjectiveName || objective.text,
                            ObjectiveValue: item.ObjectiveValue || objective.ObjectiveValue,
                            ThemeName: themeName
                        });

                    });

                });

            });

            return flattened.sort((a, b) => a.DisplayOrder - b.DisplayOrder);

        };

        const flatData = flattenData(component.Results?.Data || []);
        const allQuestions = flatData;

        const getScore = (item: any) => {

            const share = item.Share || '';

            if (!share || share === null) {

                return (
                    <span aria-label={formatMessage({ id: 'unanswered.f25b4805', defaultMessage: 'Unanswered' })} title={formatMessage({ id: 'unanswered.f25b4805', defaultMessage: 'Unanswered' })}>
                        <i
                            aria-hidden='true'
                            className='fa-solid fa-circle-minus fa-xl data-item-icon'
                            style={{ color: item.Color || '#6c757d' }}
                        />
                    </span>
                );

            }

            if (share === '1/1') {

                return (
                    <span aria-label={formatMessage({ id: 'correct.d6100cc2', defaultMessage: 'Correct' })} title={formatMessage({ id: 'correct.d6100cc2', defaultMessage: 'Correct' })}>
                        <i
                            aria-hidden='true'
                            className='fa-solid fa-circle-check fa-xl data-item-icon'
                            style={{ color: item.Color || '#198754' }}
                        />
                    </span>
                );

            }

            if (share === '0/1') {

                return (
                    <span aria-label={formatMessage({ id: 'incorrect.2a901341', defaultMessage: 'Incorrect' })} title={formatMessage({ id: 'incorrect.2a901341', defaultMessage: 'Incorrect' })}>
                        <i
                            aria-hidden='true'
                            className='fa-solid fa-circle-x fa-xl data-item-icon'
                            style={{ color: item.Color || '#dc3545' }}
                        />
                    </span>
                );

            }

            // Default fallback for any other value
            return (
                <span aria-label={formatMessage({ id: 'unanswered.f25b4805', defaultMessage: 'Unanswered' })} title={formatMessage({ id: 'unanswered.f25b4805', defaultMessage: 'Unanswered' })}>
                    <i
                        aria-hidden='true'
                        className='fa-solid fa-circle-minus fa-xl data-item-icon'
                        style={{ color: item.Color || '#6c757d' }}
                    />
                </span>
            );

        };

        const convertedComponent = {
            ...component, Results: {
                ...component.Results,
                Columns: [
                    {
                        cell: ({ dataItem }: any) => (
                            <td className='text-center'>
                                {getScore(dataItem)}
                            </td>
                        ),
                        field: 'Share',
                        title: formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' })
                    },
                    {
                        cell: ({ dataItem }: any) => (
                            <td className='text-start text-nowrap'>
                                {dataItem.QuestionId}
                            </td>
                        ),
                        field: 'QuestionId',
                        title: intl.formatMessage({ id: 'qid.58366cbe', defaultMessage: 'QID' })
                    },
                    {
                        cell: ({ dataItem }: any) => (
                            <td className='text-start'>
                                {dataItem.QuestionName}
                            </td>
                        ),
                        field: 'QuestionName',
                        title: formatMessage({ id: 'question_name.b3f17db0', defaultMessage: 'Question Name' })
                    },
                    {
                        cell: ({ dataItem }: any) => (
                            <td>
                                <SanitizedQuestionStem html={dataItem.Description} />
                            </td>
                        ),
                        field: 'Description',
                        title: formatMessage({ id: 'question_stem.6af71794', defaultMessage: 'Question Stem' })
                    },
                    {
                        cell: ({ dataItem }: any) => (
                            <td>
                                <Button
                                    onClick={e => {

                                        const index = allQuestions.findIndex(i => i.Title === dataItem.Title);
                                        questionViewerRef.current.create({
                                            allQuestions,
                                            isInAppTest,
                                            modalTitle: dataItem.Title,
                                            objectivePassPercentage: dataItem.Share,
                                            questionId: dataItem.QuestionId,
                                            questionIndex: index,
                                            resultId,
                                            studentProfileId,
                                            triggerElement: e.currentTarget
                                        });

                                    }}
                                    aria-label={formatMessage({ id: 'view_question.88a88fc5', defaultMessage: 'View Question' })}
                                    className='link-underline link-underline-opacity-0 link-underline-opacity-0-hover'
                                    title={formatMessage({ id: 'view_question.88a88fc5', defaultMessage: 'View Question' })}
                                    variant='link'
                                >
                                    <i aria-hidden='true' className="fa-solid fa-eye fa-xl text-primary"></i>
                                </Button>
                            </td>
                        ),
                        field: 'ViewQuestion',
                        title: formatMessage({ id: 'question.e7d7defa', defaultMessage: 'Question' })
                    }
                ],
                Data: flatData
            }
        } as ReportComponent;
        return convertedComponent;

    };

    const printModal = () => {

        const modal = document.querySelector('[role="dialog"].modal');
        modal.classList.add('printing');
        window.print();
        modal.classList.remove('printing');

    };

    const renderComponents = (component: ReportComponent, loading: boolean, index: number, isPrinting = false) => {

        const data = component.Results?.Data[0];
        const resultId = reportSections[0].Components[0].Results?.Data[0].ResultId;
        const testType = reportSections[0].Components[0].Results?.Data[0].TestType;

        // isInAppTest determines which question runner to use 
        const isInAppTest =
            testType === TestType.Application ||
            testType === TestType.AdobeLITA ||
            testType === TestType.multiproject;
        const url = new URL(window.location.href);
        const params = url.searchParams;
        const reportId = getReportIdFromPath();

        if (reportId !== '62') {

            studentProfileId = params.get('studentId') ?? params.get('ScopeObjectId');

        }

        if (component.Type === 'Arc') {

            component = {
                ...component,
                Results: {
                    ...component.Results,
                    Data: [
                        {
                            Color: data.ScoreStatus.Color,
                            MaxValue: 1000,
                            PreviousValue: data.PreviousScore,
                            Title: data.ScoreStatus.Share === '1' ? formatMessage({ id: 'passed.d269c364', defaultMessage: 'Passed', description: 'Formulario' }) : formatMessage({ id: 'failed.2afe41d7', defaultMessage: 'Failed', description: 'Formulario' }),
                            Value: data.Score
                        }
                    ]
                },
                Title: formatMessage({ id: 'my_score.9d18e7ae', defaultMessage: 'My Score' })
            } as ReportComponent;

            return (
                <GeneralGauge component={component} index={1} isSubReport={false} loading={false} />
            );

        }

        if (component.Type === 'ListOfInfoBox') {

            component = {
                ...component,
                Results: {
                    ...component.Results,
                    SeriesList: [
                        {
                            Data: [
                                {
                                    Share: data.CategoryName,
                                    Title: formatMessage({ id: 'category.f324da36', defaultMessage: 'Category' })
                                },
                                {
                                    Share: data.ProductName,
                                    Title: formatMessage({ id: 'product.cf8158fa', defaultMessage: 'Product', description: 'Gridview header' })
                                },
                                {
                                    Share: data.mode === 'Training' ? formatMessage({ id: 'training.1487a481', defaultMessage: 'Training', description: 'training mode' }) : formatMessage({ id: 'testing.05ed1ac8', defaultMessage: 'Testing', description: 'testing mode' }),
                                    Title: formatMessage({ id: 'mode.71e0dd61', defaultMessage: 'Mode', description: 'Formulario' })
                                },
                                {
                                    Share: data.CutScore,
                                    Title: formatMessage({ id: 'passing_score.5fe03d6f', defaultMessage: 'Passing Score' })
                                },
                                {
                                    Share: data.ElapsedTime,
                                    Title: formatMessage({ id: 'time_spent.da775c0c', defaultMessage: 'Time Spent' })
                                },
                                {
                                    Share: intl.formatDate(new Date(data.FinishDate), { day: 'numeric', month: 'short', year: 'numeric' }),
                                    Title: formatMessage({ id: 'date_finished.70b7f1cf', defaultMessage: 'Date Finished' })
                                }
                            ]
                        }

                    ]
                },
                Title: formatMessage({ id: 'test_details.88f73a4e', defaultMessage: 'Test Details' })
            } as ReportComponent;

            return (
                <ChartInfoBox component={component} index={index} isSubReport={true} loading={false} />
            );

        }

        if (component.Type === 'Table') {

            if (component.ConvertToFlatList) {

                component = convertToFlatList(component, resultId, isInAppTest);

            }
            return (
                <div className="d-print-none">
                    <TableGeneric
                        allowExport={false}
                        component={component}
                        index={index}
                        isSubReport={true}
                        key={index}
                        loading={loading}
                        scrollable="none"
                        setCurrentSubReport={val => setState({ currentSubReport: val })}
                    />
                </div>
            );

        }

        if (component.Type === 'SkillTree') {

            const generateFakeProgressBars = (countOfProgressBars: number) => {

                const fakeArrayCount = [];
                for (let i = 0; i < countOfProgressBars; i++) {

                    fakeArrayCount.push(i);

                }

                return fakeArrayCount.map(i => (
                    <Row className="mb-4 d-flex flex-column align-items-center" key={i}>
                        <Col className="skeleton gray progress-bar-fake p-2 mb-1 w-75"></Col>
                        <Col className="skeleton gray progress-bar-fake p-2 w-75"></Col>
                    </Row>
                ));

            };

            const allQuestions = getAllQuestions(component.Results?.Data);
            const treeData = isPrinting ? expandSkillTreeForPrinting(component.Results?.Data) : component.Results?.Data;

            return (
                <SkillTree
                    allQuestions={allQuestions}
                    component={component}
                    generateFakeProgressBars={generateFakeProgressBars}
                    index={index}
                    isInAppTest={isInAppTest}
                    isPrinting={isPrinting}
                    loading={loading}
                    questionViewerRef={questionViewerRef}
                    resultId={resultId}
                    studentProfileId={studentProfileId}
                    treeData={treeData}
                />
            );

        }

    };

    const expandSkillTreeForPrinting = (data: any[]): any[] => (data || []).map(item => ({
        ...item,
        expanded: true,
        items: Array.isArray(item.items)
            ? expandSkillTreeForPrinting(item.items)
            : item.items
    }));

    const getTitleBySection = (tab: ReportSection) => {

        switch (tab.SectionId) {

            case 2:
                return formatMessage({ id: 'objectives.de5845c5', defaultMessage: 'Objectives' });
            case 3:
                return formatMessage({ id: 'questions.5d4620a7', defaultMessage: 'Questions', description: ':(' });
            case 1:
            default:
                return formatMessage({ id: 'summary.b52cf991', defaultMessage: 'Summary' });

        }

    };

    return (
        <>
            {new URL(window.location.href).searchParams.get('ReportId') === '62' && (
                <div className="align-items-center d-flex justify-content-between">
                    <div className="mb-2">
                        <div className="large">
                            {reportSections[0].Components[0].Results?.Data[0].TestName}
                        </div>
                        <div>
                            <span className="fw-bold">
                                <FormattedMessage defaultMessage="Student:" id="student.a2effdfe" />
                            </span>
                            {' '}
                            {reportSections[0].Components[0].Results?.Data[0].StudentName}
                        </div>
                    </div>
                    <div>
                        <Button
                            className="fw-bold float-end d-print-none"
                            onClick={printModal}
                            size='sm'
                            variant='outline-primary'
                        >
                            <i aria-hidden='true' className="fa-regular fa-print me-2"></i>
                            <FormattedMessage defaultMessage="Print" id="print.1fd3b13a" />
                        </Button>
                    </div>
                </div>
            )}

            <GMetrixDialog actionButtonClick={() => setState({ showDetailsModal: false })} actionButtonDisabled={false} actionButtonText={formatMessage({ id: 'ok.981b7e92', defaultMessage: 'Ok' })} dialogLabel={formatMessage({ id: 'question_details.a2e0bbb7', defaultMessage: 'Question Details' })} onCancel={() => setState({ showDetailsModal: false })} onClose={() => setState({ showDetailsModal: false })} show={showDetailsModal} >
                <div dangerouslySetInnerHTML={{ __html: currentItem?.Description ?? '' }}></div>
            </GMetrixDialog>
            {
                reportSections.map((section, reportSectionIndex) => {

                    if (tempGroup.length > 0) {

                        currentTabGroup = [];

                    }

                    if (section.TabGroup) {

                        currentTabGroup.push(section);
                        if (reportSectionIndex === reportSections.length - 1) {

                            tempGroup = { ...currentTabGroup };

                            return (
                                <GMetrixTabs
                                    className="mb-2 d-print-none"
                                    key={`${reportSectionIndex}-extra`}
                                    onSelect={handleSelect}
                                    selected={selected}
                                >
                                    {currentTabGroup.map((tab, index) => (
                                        <GMetrixTabsTab key={index} title={getTitleBySection(tab)}>
                                            <Container fluid className="component-wrapper mb-2">
                                                <Row className="bg-white">
                                                    {tab.Components.map((component, componentSectionIndex) => renderComponents(
                                                        component,
                                                        false,
                                                        componentSectionIndex
                                                    ))}
                                                </Row>
                                            </Container>
                                        </GMetrixTabsTab>
                                    ))}
                                </GMetrixTabs>
                            );

                        }
                        return null;

                    }
                    if (currentTabGroup.length > 0) {

                        tempGroup = { ...currentTabGroup };

                    }

                    return (
                        <GMetrixTabs
                            className="mb-2 d-print-none"
                            key={reportSectionIndex}
                            onSelect={handleSelect}
                            selected={selected}
                        >
                            {currentTabGroup.map((tab, indexGroup) => (
                                <GMetrixTabsTab key={indexGroup} title={tab.Title}>
                                    <Container fluid className="component-wrapper mb-2 border rounded">
                                        <Row>
                                            {tab.Components.map((component, componentSectionIndex) => renderComponents(
                                                component,
                                                false,
                                                componentSectionIndex
                                            ))}
                                        </Row>
                                    </Container>
                                </GMetrixTabsTab>
                            ))}
                        </GMetrixTabs>
                    );

                })
            }

            <div className='d-none d-print-block'>
                {/* This is only for printing the score report because Kendo's tabstrip adds extra pages */}
                {reportSections
                    .filter(section => section.TabGroup)
                    .map((tab, indexGroup) => (
                        <Container fluid key={tab.SectionId ?? indexGroup}>
                            <Row className="print-only">
                                {tab.Components.map((component, componentSectionIndex) => renderComponents(component, false, componentSectionIndex, true)
                                )}
                            </Row>
                        </Container>
                    ))}
            </div>

            <QuestionViewer ref={questionViewerRef} />
        </>
    );

};